import { useState } from "react";
import { Popover } from "antd";
import { DownOutlined, LoadingOutlined, RightOutlined } from "@ant-design/icons";
import AudioCheatingAnalysis from "../AudioCheatingAnalysis";
import { capitalizeTitle } from "src/utils/helperFunctions";
import { clockOf } from "./rules/format";
import { useReviewClock } from "./clock";

/*
 * The audio in one line at the foot of the timeline, figures as the bar shows
 * them, so it stays in view with the recording (4.0 keeps the player and the
 * timeline in one view): how much of the round was speech, how much came
 * outside the answers, and the audio review's verdict. "Transcript" opens a
 * drawer under it with the transcript on the clock, the other figures and the
 * report's own audio analysis (AudioCheatingAnalysis, reused). The line ends
 * with what the platform never captures, behind a chip.
 */

const fmtMs = (ms) => {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return "—";
  const s = n / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  if (m < 60) return `${m}m ${r}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m ${r}s`;
};
const pctOf = (part, whole) => (whole && Number.isFinite(part) ? Math.round((part / whole) * 100) : null);
/** A figure in ms, whichever way the metrics carry it (…Ms, or …Sec as the timeline sends them). */
const msOf = (m, ms, sec) => {
  if (m[ms] != null && Number.isFinite(Number(m[ms]))) return Number(m[ms]);
  if (sec && m[sec] != null && Number.isFinite(Number(m[sec]))) return Number(m[sec]) * 1000;
  return null;
};

/** The audio's figures, from timeline.audio.metrics; null when it holds none. */
export function audioFigures(audio) {
  const m = (audio && audio.metrics) || {};
  const total = msOf(m, "totalAudioMs", "totalAudioSec");
  const speech = msOf(m, "totalSpeechMs", "speechSec");
  const answers = msOf(m, "speechQuestionSpeechMs", "speechQuestionSpeechSec");
  const outside = msOf(m, "unexpectedSpeechMs", "unexpectedSpeechSec");
  if (total == null && speech == null && outside == null) return null;
  return { total, speech, answers, outside };
}

function NotCaptured({ items }) {
  if (!items || !items.length) return null;
  const list = (
    <ul className="proctoring-review__nc-list">
      {items.map((x) => (
        <li key={x.k}><b>{x.n}</b><span>{x.why || "Not recorded"}</span></li>
      ))}
    </ul>
  );
  return (
    <Popover content={list} title="Not recorded by the platform" trigger="click" placement="topRight">
      <button type="button" className="proctoring-review__nc-chip" title="What the platform does not record">
        {`Not captured ${items.length}`}
        <DownOutlined />
      </button>
    </Popover>
  );
}

function Lines({ lines }) {
  const c = useReviewClock();
  if (!lines.length) return <p className="proctoring-review__ev-muted">No transcript for this round.</p>;
  return (
    <ol className="proctoring-review__transcript-lines">
      {lines.map((l) => {
        const on = c.engaged && c.t >= l.from && c.t < l.to;
        return (
          <li key={l.line || `${l.from}-${l.text}`} className={on ? "is-on" : ""}>
            <button type="button" onClick={() => c.seek(l.from)} title="Go to this line">
              <span>{clockOf(l.from)}</span>
              {l.text}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * @param {Object} props
 * @param {Object|null} props.audio                  timeline.audio
 * @param {Object|null} props.audioProctoringReport  the report's own audio analysis
 * @param {Array} props.notCaptured                  {k, n, why}: never captured by the platform
 */
export default function AudioSummary({ audio, audioProctoringReport, notCaptured = [] }) {
  const [open, setOpen] = useState(false);
  const lines = ((audio && audio.transcript) || []).filter((l) => l && Number.isFinite(l.from) && l.text);
  const hasAudio = !!(audio || audioProctoringReport);
  if (!hasAudio && !notCaptured.length) return null;

  const f = hasAudio ? audioFigures(audio) : null;
  const review = audio && audio.review;
  const processing = !!audio && audio.vadCompleted === false;
  const failed = !!audio && audio.status === "failed";
  const canOpen = hasAudio;

  return (
    <div className={`proctoring-review__strip${open ? " is-open" : ""}`}>
      <div className="proctoring-review__strip-line">
        {hasAudio && (
          <dl className="proctoring-review__strip-facts" aria-label="Audio">
            {failed && <div><dt>Audio</dt><dd className="proctoring-review__ink--bad">Could not be analysed</dd></div>}
            {!failed && processing && <div><dt>Speech</dt><dd><LoadingOutlined /> Still processing</dd></div>}
            {!failed && !processing && f && (
              <>
                <div title={pctOf(f.speech, f.total) != null ? `${pctOf(f.speech, f.total)}% of the audio was speech` : undefined}>
                  <dt>Speech</dt>
                  <dd>{fmtMs(f.speech)}{pctOf(f.speech, f.total) != null && <small>{` · ${pctOf(f.speech, f.total)}%`}</small>}</dd>
                </div>
                <div title={pctOf(f.outside, f.speech) != null ? `${pctOf(f.outside, f.speech)}% of the speech came outside the spoken answers` : undefined}>
                  <dt>Outside answers</dt>
                  <dd className={f.outside > 0 ? "proctoring-review__ink--warn" : ""}>
                    {fmtMs(f.outside)}{pctOf(f.outside, f.speech) != null && <small>{` · ${pctOf(f.outside, f.speech)}%`}</small>}
                  </dd>
                </div>
              </>
            )}
            {review && review.verdict && (
              <div title={review.confidence ? `The audio review's verdict, with ${review.confidence} confidence` : "The audio review's verdict"}>
                <dt>Audio review</dt>
                <dd className={review.verdict === "suspicious" ? "proctoring-review__ink--bad" : ""}>
                  {capitalizeTitle(review.verdict)}{review.confidence && <small>{` · ${review.confidence}`}</small>}
                </dd>
              </div>
            )}
          </dl>
        )}
        {canOpen && (
          <button type="button" className="proctoring-review__strip-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            Transcript
            {lines.length > 0 && <span className="proctoring-review__strip-count">{` · ${lines.length} ${lines.length === 1 ? "line" : "lines"}`}</span>}
            {open ? <DownOutlined /> : <RightOutlined />}
          </button>
        )}
        <span className="proctoring-review__grow" />
        <NotCaptured items={notCaptured} />
      </div>
      {open && (
        <div className="proctoring-review__strip-drawer">
          <p className="proctoring-review__ev-muted">
            {f
              ? `${fmtMs(f.total)} of audio recorded; ${fmtMs(f.answers)} of the speech came in spoken answers. `
              : ""}
            Speech detection hears that someone spoke, not who: there is no speaker detection.
          </p>
          <Lines lines={lines} />
          <AudioCheatingAnalysis audioProctoringReport={audioProctoringReport} hideTranscript />
        </div>
      )}
    </div>
  );
}
